class CartDiscountBlock extends HTMLElement {
  static pendingSuccess = false;
  // Remembered across re-renders of the cart drawer (the whole block is rebuilt from the
  // sections API after every cart change), so an open coupon panel stays open.
  static panelOpen = false;

  constructor() {
    super();
    this.form = this.querySelector('.cart-discount-form');
    this.input = this.querySelector('input[name="cart-discount-field"]');
    this.submitBtn = this.querySelector('.cart-discount-form__submit');
    this.spinner = this.querySelector('.cart-discount-form__submit .loading-overlay__spinner');
    this.submitLabel = this.querySelector('.cart-discount-form__submit-label');
    this.errorEmpty = this.querySelector('.cart-discount-form__error--empty');
    this.errorInvalid = this.querySelector('.cart-discount-form__error--invalid');
    this.errorDuplicate = this.querySelector('.cart-discount-form__error--duplicate');
    this.successMsg = this.querySelector('.cart-discount-form__success');
    this.toggleBtn = this.querySelector('.cart-discount__toggle');
    this.panel = this.querySelector('.cart-discount__panel');
    this.submitting = false;

    if (this.toggleBtn && this.panel) {
      this.toggleBtn.addEventListener('click', () => this.setOpen(!this.isOpen()));
      if (CartDiscountBlock.panelOpen) this.setOpen(true, false);
    }

    this.hideErrors();
    if (this.form) this.form.addEventListener('submit', this.handleSubmit.bind(this));
    this.addEventListener('click', this.handleClick.bind(this));
    if (this.input) this.input.addEventListener('input', this.hideErrors.bind(this));

    if (CartDiscountBlock.pendingSuccess) {
      CartDiscountBlock.pendingSuccess = false;
      this.showSuccess();
    }
  }

  isOpen() {
    return !!this.panel && !this.panel.hidden;
  }

  // Touch devices open the on-screen keyboard as soon as an input is focused, which hides the
  // field the shopper just revealed. Only auto-focus where there is a mouse/trackpad.
  static get hasFinePointer() {
    try {
      return !(window.matchMedia('(hover: none)').matches || window.matchMedia('(pointer: coarse)').matches);
    } catch (e) {
      return false;
    }
  }

  setOpen(open, focus = true) {
    if (!this.panel || !this.toggleBtn) return;
    this.panel.hidden = !open;
    this.classList.toggle('cart-discount--open', open);
    this.toggleBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    CartDiscountBlock.panelOpen = open;
    if (!open) return;
    // Bring the revealed field into view (the block sits near the bottom of the drawer).
    if (focus) {
      try { this.panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (e) { /* ignore */ }
    }
    if (focus && this.input && CartDiscountBlock.hasFinePointer) {
      try { this.input.focus({ preventScroll: true }); } catch (e) { this.input.focus(); }
    } else if (focus) {
      this.guardTouchFocus();
    }
  }

  // Touch devices: the drawer footer grows upward when the panel opens, so the revealed field
  // lands right under the finger that tapped the toggle and iOS hands the tap to the field,
  // which opens the keyboard. Ignore taps/focus on the field for a moment after opening; the
  // shopper taps the field when they want to type.
  guardTouchFocus() {
    const field = this.panel && this.panel.querySelector('.field');
    if (!field) return;
    const until = Date.now() + 600;
    const types = ['touchstart', 'pointerdown', 'mousedown', 'click'];
    const opts = { capture: true, passive: false };
    const block = (event) => {
      if (Date.now() > until || !field.contains(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
    };
    const unfocus = (event) => {
      if (Date.now() <= until && event.target === this.input) {
        try { this.input.blur(); } catch (e) { /* ignore */ }
      }
    };
    types.forEach((t) => this.addEventListener(t, block, opts));
    this.addEventListener('focusin', unfocus, true);
    setTimeout(() => {
      types.forEach((t) => this.removeEventListener(t, block, opts));
      this.removeEventListener('focusin', unfocus, true);
    }, 650);
  }

  hideErrors() {
    if (this.errorEmpty) this.errorEmpty.style.display = 'none';
    if (this.errorInvalid) this.errorInvalid.style.display = 'none';
    if (this.errorDuplicate) this.errorDuplicate.style.display = 'none';
  }

  showError(which) {
    this.hideErrors();
    const map = { empty: this.errorEmpty, invalid: this.errorInvalid, duplicate: this.errorDuplicate };
    const target = map[which];
    if (target) target.style.display = 'block';
  }

  showSuccess() {
    if (!this.successMsg || this.dataset.successEnabled !== 'true') return;
    this.successMsg.style.display = 'block';
    const duration = parseInt(this.dataset.successDuration, 10) || 2000;
    clearTimeout(this._successTimer);
    this._successTimer = setTimeout(() => {
      if (this.successMsg) this.successMsg.style.display = 'none';
    }, duration);
  }

  setLoading(loading) {
    this.submitting = loading;
    if (this.submitBtn) {
      this.submitBtn.disabled = loading;
      this.submitBtn.classList.toggle('loading', loading);
    }
    if (this.spinner) this.spinner.classList.toggle('hidden', !loading);
    if (this.submitLabel) this.submitLabel.style.visibility = loading ? 'hidden' : '';
  }

  existingCodes() {
    const fromAttr = (this.dataset.appliedCodes || '').trim();
    if (fromAttr) return fromAttr.split(',').map(c => c.trim()).filter(Boolean);
    return Array.from(this.querySelectorAll('.cart-discount__pill'))
      .map(p => p.dataset.discountCode)
      .filter(Boolean);
  }

  async handleSubmit(event) {
    event.preventDefault();
    if (this.submitting) return;
    this.hideErrors();
    const code = (this.input.value || '').trim();
    if (!code) {
      this.showError('empty');
      return;
    }
    const existing = this.existingCodes();
    if (existing.some(c => c.toLowerCase() === code.toLowerCase())) {
      this.showError('duplicate');
      return;
    }
    this.setLoading(true);
    const allCodes = [...existing, code].join(',');
    const data = await this.postDiscount(allCodes);
    if (!data) {
      this.setLoading(false);
      return;
    }
    const submitted = (data.discount_codes || []).find(d => d.code.toLowerCase() === code.toLowerCase());
    const applicable = submitted && submitted.applicable !== false;
    const newApplicableCodes = (data.discount_codes || []).filter(d => d.applicable).map(d => d.code.toLowerCase());
    const wasNewCodeApplied = newApplicableCodes.length > existing.length;
    if (!applicable || !wasNewCodeApplied) {
      this.showError('invalid');
      this.input.value = '';
      await this.postDiscount(existing.join(','));
      this.setLoading(false);
      return;
    }
    this.input.value = '';
    CartDiscountBlock.pendingSuccess = true;
    // Collapse the panel after a successful apply: the coupon now shows in the totals.
    CartDiscountBlock.panelOpen = false;
    this.refreshCart(data);
  }

  handleClick(event) {
    const removeBtn = event.target.closest('.cart-discount__pill-remove');
    if (!removeBtn) return;
    event.preventDefault();
    if (this.submitting) return;
    const pill = removeBtn.closest('.cart-discount__pill');
    if (!pill) return;
    const code = pill.dataset.discountCode;
    if (code) this.removeDiscount(code);
  }

  async removeDiscount(code) {
    this.setLoading(true);
    const remaining = this.existingCodes().filter(c => c !== code);
    const data = await this.postDiscount(remaining.join(','));
    if (data) {
      this.refreshCart(data);
    } else {
      this.setLoading(false);
    }
  }

  async postDiscount(discountStr) {
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          discount: discountStr,
          sections: ['cart-drawer', 'cart-icon-bubble']
        })
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (err) {
      return null;
    }
  }

  refreshCart(cartData) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function') {
      drawer.renderContents(cartData);
      const drawerEl = document.querySelector('cart-drawer.drawer');
      if (drawerEl) drawerEl.classList.remove('is-empty');
    }
  }
}
if (!customElements.get('cart-discount-block')) {
  customElements.define('cart-discount-block', CartDiscountBlock);
}
